//@ts-strict-ignore
// ts-strict causes a lot of errors in application-menu-base.directive and it has to be dealt together with all menu implementations
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { LMSpecService } from '@services/lm-spec.service';
import { LMContextService } from "@services/lm-context.service";
import { LMLoggerService } from "@services/lm-logger.service";

type LMWebAppMenuSpecs = { [key: string]: LMWebAppMenuSpec };
type LMWebAppMenuSubjects = { [key: string]: Subject<LMWebAppMenuSpec> };
@Injectable()
export class LMWebAppService {
  private webAppMenuSpecs: LMWebAppMenuSpecs = {};
  private webAppMenuSubjects: LMWebAppMenuSubjects = {};

  constructor(
    private lmLoggerService: LMLoggerService,
    private lmContextService : LMContextService,
    private lmSpecService: LMSpecService) {}


  public getWebAppMenuSpecWithUrl(webAppName: string, url: string): Observable<LMWebAppMenuSpec> {
    if (this.webAppMenuSpecs && this.webAppMenuSpecs[webAppName]) {
      const updatedWebAppSpec = this.cloneWebAppMenuSpec(this.webAppMenuSpecs[webAppName]);
      // need to make sure the flags for flyout are correct, in the case of unified resetting the context to the
      // skinny web app on each app menu click
      if (this.lmContextService.isUnified()) {
        this.updateWebAppSpecInContext(updatedWebAppSpec);
      }
      this.lmLoggerService.log([ 'LMWebappService', 'returning from service cache with getWebAppMenuSpecWithUrl for: ' + webAppName ]);
      return of(updatedWebAppSpec);
    }

    if (this.webAppMenuSubjects && this.webAppMenuSubjects[webAppName]) {
      this.lmLoggerService.log([ 'LMWebappService', 'waiting for webAppMenuSpecs with getWebAppMenuSpecWithUrl for : ' + webAppName ]);
      return this.webAppMenuSubjects[webAppName];
    }
    this.lmLoggerService.log([ 'LMWebappService', 'fetching webAppMenuSpecs with getWebAppMenuSpecWithUrl for : ' + webAppName ]);

    const webAppMenuSpec$ = new Subject<LMWebAppMenuSpec | undefined>();
    this.webAppMenuSubjects[webAppName] = webAppMenuSpec$;

    this.lmSpecService.getApplicationMenuSpec(`${url}&allLevels=true`)
      .subscribe({
        next: (appMenuSpec) => {
          this.handleWebAppMenuSpecResponse(webAppName, appMenuSpec, webAppMenuSpec$);
        },
        error: (error) => {
          webAppMenuSpec$.error(error);
          webAppMenuSpec$.complete();
          delete this.webAppMenuSubjects[webAppName];
        }
      });
    return webAppMenuSpec$.asObservable();

  }
  public getWebAppMenuSpec(webAppName: string): Observable<LMWebAppMenuSpec> {
    // If webapp is in the collection, return the web spec

    if (this.webAppMenuSpecs && this.webAppMenuSpecs[webAppName]) {
      const updatedWebAppSpec = this.cloneWebAppMenuSpec(this.webAppMenuSpecs[webAppName]);
      // need to make sure the flags for flyout are correct, in the case of unified resetting the context to the
      // skinny web app on each app menu click
      if (this.lmContextService.isUnified()) {
        this.updateWebAppSpecInContext(updatedWebAppSpec);
      }
      this.lmLoggerService.log([ 'LMWebappService', 'returning from service cache with getWebAppMenuSpec for: ' + webAppName ]);
      return of(updatedWebAppSpec);
    }

    if (this.webAppMenuSubjects && this.webAppMenuSubjects[webAppName]) {
      this.lmLoggerService.log([ 'LMWebappService', 'waiting for webAppMenuSpecs with webAppMenuSpecs for : ' + webAppName ]);
      return this.webAppMenuSubjects[webAppName];
    }
    this.lmLoggerService.log([ 'LMWebappService', 'fetching webAppMenuSpecs with webAppMenuSpecs for : ' + webAppName ]);

    const webAppSpecList = this.lmContextService.getAccessibleWebApps(); // just the list of web apps!
    const webApp = webAppSpecList.find(w => w.webAppName === webAppName);
    if (!webApp?.navigationMenuUrl) {
      // throw an error?
      return of({});
    }

    const webAppMenuSpec$ = new Subject<LMWebAppMenuSpec | undefined>();
    this.webAppMenuSubjects[webAppName] = webAppMenuSpec$;

    this.lmSpecService.getApplicationMenuSpec(`${webApp.navigationMenuUrl}&allLevels=true`)
      .subscribe({
        next: (appMenuSpec) => {
          this.handleWebAppMenuSpecResponse(webAppName, appMenuSpec, webAppMenuSpec$);
        },
        error: (error) => {
          webAppMenuSpec$.error(error);
          webAppMenuSpec$.complete();
          delete this.webAppMenuSubjects[webAppName];
        }
      });
    return webAppMenuSpec$.asObservable();
  }

  private handleWebAppMenuSpecResponse(webAppName: string, response: LMWebAppMenuSpec, subject: Subject<LMWebAppMenuSpec>) {
    this.webAppMenuSpecs[webAppName] = response;
    const webAppSpecMenu = this.cloneWebAppMenuSpec(response);
    this.updateWebAppSpecInContext(webAppSpecMenu);
    subject.next(webAppSpecMenu);
    subject.complete();
    delete this.webAppMenuSubjects[webAppName];
  }

  private cloneWebAppMenuSpec(response: LMWebAppMenuSpec) {
    const clone: LMWebAppMenuSpec = JSON.parse(JSON.stringify(response));
    return clone;
  }

  public getAllCachedWebApps(): LMWebAppMenuSpec[] {
    const allCachedWebApps: LMWebAppMenuSpec[] = [];
    Object.keys(this.webAppMenuSpecs).map(k => allCachedWebApps.push(JSON.parse(JSON.stringify(this.webAppMenuSpecs[k]))));
    return allCachedWebApps;
  }

  public isPagePanelInaccessible(pagePanelSpec: LMPagePanelSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }
    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        pagePanelSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildPagePanelId()
      const propertyId =
          'pagePanel.' + menuName + '.' + pagePanelSpec.menuSecurityId;
      const homePagePropertyId = 'pagePanel.' + pagePanelSpec.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }
      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isPagePaneInaccessible(pagePaneSpec: LMPagePaneSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        pagePaneSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildPagePaneId()
      const propertyId =
          'pagePanel.' + menuName + '.' + pagePaneSpec.menuSecurityId;
      const homePagePropertyId = 'pagePanel.' + pagePaneSpec.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }
      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isPageMenuItemInaccessible(formLayoutField: LMFormLayoutField | LMFormLayoutMenu): boolean {
    if (!formLayoutField) {
      return false;
    }

    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        formLayoutField.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildFullPageMenuItemId()
      const propertyId =
          'pageMenuItem.' + menuName + '.' + formLayoutField.menuSecurityId;
      const homePagePropertyId =
          'pageMenuItem.' + formLayoutField.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }

      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isFormPanelInaccessible(formPanelSpec: LMCompositePanelSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        formPanelSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildFullFormPanelId()
      const propertyId =
          'formPanel.' + menuName + '.' + formPanelSpec.menuSecurityId;
      return webAppSpec.inaccessibleItems.includes(propertyId);
    } else {
      return false;
    }
  }

  private updateWebAppSpecInContext(webAppSpec: LMWebAppSpec): void {
    if (webAppSpec.showFormFlyout || webAppSpec.hideContextualLinks)
      this.lmContextService.updateWebAppSpecForFormOverlay(webAppSpec.webAppName, webAppSpec.showFormFlyout, webAppSpec.hideContextualLinks);
  }
}
